<script lang="ts">
/**
 * AddressBar component
 * This component is used to display the address bar for the operation block
 * It is used to display the path, method, server, and history for the operation
 */
export default {
  name: 'AddressBar',
}
export type AddressBarProps = {
  /** Current request path */
  path: string
  /** Current request method */
  method: HttpMethodType
  /** Currently selected server */
  server: ServerObject | null
  /** Server list available for operation/document */
  servers: ServerObject[]
  /** List of request history */
  history: History[]
  /** Client layout */
  layout: ClientLayout
  /** Event bus */
  eventBus: WorkspaceEventBus
  /** Environment */
  environment: XScalarEnvironment
  /** Meta information for the server */
  serverMeta: ServerMeta
}
</script>
<script setup lang="ts">
import {
  ScalarButton,
  ScalarIcon,
  ScalarWrappingText,
} from '@scalar/components'
import { getSelector } from '@scalar/helpers/dom/get-selector'
import { REQUEST_METHODS } from '@scalar/helpers/http/http-info'
import type { HttpMethod as HttpMethodType } from '@scalar/helpers/http/http-methods'
import { replaceEnvVariables } from '@scalar/helpers/regex/replace-variables'
import { extractServerFromPath } from '@scalar/helpers/url/extract-server-from-path'
import { ScalarIconCopy, ScalarIconWarningCircle } from '@scalar/icons'
import { EditorView } from '@scalar/use-codemirror'
import { useClipboard } from '@scalar/use-hooks/useClipboard'
import type {
  ApiReferenceEvents,
  ServerMeta,
  WorkspaceEventBus,
} from '@scalar/workspace-store/events'
import {
  getEnvironmentVariables,
  getResolvedUrl,
} from '@scalar/workspace-store/request-example'
import type { XScalarEnvironment } from '@scalar/workspace-store/schemas/extensions/document/x-scalar-environments'
import type { ServerObject } from '@scalar/workspace-store/schemas/v3.1/strict/openapi-document'
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  useId,
  useTemplateRef,
  watch,
} from 'vue'

import { HttpMethod } from '@/components/HttpMethod'
import { useLoadingAnimation } from '@/v2/blocks/scalar-address-bar-block/hooks/use-loading-animation'
import { CodeInput } from '@/v2/components/code-input'
import { ServerDropdown } from '@/v2/components/server'
import type { ClientLayout } from '@/v2/types/layout'

import AddressBarHistory, { type History } from './AddressBarHistory.vue'

const {
  path,
  method,
  layout,
  eventBus,
  history,
  server,
  servers,
  environment,
  serverMeta,
} = defineProps<AddressBarProps>()

const emit = defineEmits<{
  /** Execute the current operation example */
  (e: 'execute'): void
  /** Select a request history item by index */
  (e: 'select:history:item', payload: { index: number }): void
}>()

const id = useId()
const { percentage, startLoading, stopLoading, isLoading } =
  useLoadingAnimation()

/** Calculate the style for the address bar */
const style = computed(() => ({
  backgroundColor: `color-mix(in srgb, transparent 90%, ${REQUEST_METHODS[method].colorVar})`,
  transform: `translate3d(-${percentage.value}%,0,0)`,
}))

/** Keeps the cursor visible past the fade-right overlay while typing */
const addressBarScrollMargins = EditorView.scrollMargins.of(() => ({
  right: 24,
}))

const pathConflict = ref<string | null>(null)
const methodConflict = ref<HttpMethodType | null>(null)

/** Whether there is a path or method conflict */
const hasConflict = computed(() => methodConflict.value || pathConflict.value)

/** Clear conflict state when switching to a different operation */
watch(
  () => [path, method],
  () => {
    pathConflict.value = null
    methodConflict.value = null
  },
)

/** Check if the path contains a server URL, extract it, and select or add the server */
const checkForServer = (targetPath: string) => {
  const extracted = extractServerFromPath(targetPath)
  if (!extracted) {
    return targetPath
  }

  const [url, newPath] = extracted

  // Server is already selected — nothing to change
  if (url === server?.url) {
    return newPath
  }

  const matchingServer = servers.find((s) => s.url === url)

  // Select the server if it already exists in the list
  if (matchingServer) {
    eventBus.emit('server:update:selected', {
      url,
      meta: serverMeta,
    })
  }
  // Otherwise add it as a new operation-level server
  else {
    eventBus.emit('server:add:server', {
      url,
      select: true,
      meta: {
        type: 'operation',
        path,
        method,
      },
    })
  }

  return newPath
}

/** Emit the path/method update event with conflict handling */
const emitPathMethodUpdate = (
  targetMethod: HttpMethodType,
  targetPath: string,
  blurTargetSelector: string | null = null,
): void => {
  const newPath = checkForServer(targetPath)
  const normalizedPath = newPath.startsWith('/') ? newPath : `/${newPath}`

  // Update the local state of codemirror so we don't have werid path on conflict
  addressBarRef.value?.setCodeMirrorContent(normalizedPath)

  eventBus.emit('operation:update:pathMethod', {
    meta: { method, path },
    blurTargetSelector,
    payload: { method: targetMethod, path: normalizedPath },
    callback: (status, blurTargetSelector) => {
      // Clear conflicts if the operation was successful or no change was made
      if (status === 'success' || status === 'no-change') {
        methodConflict.value = null
        pathConflict.value = null
      }
      // Otherwise set the conflict if needed
      else if (status === 'conflict') {
        if (targetMethod !== method) {
          methodConflict.value = targetMethod
        }
        if (normalizedPath !== path) {
          pathConflict.value = normalizedPath
        }
      }

      // Edge case: pasting a full URL extracts the server but leaves the path unchanged.
      // The CodeMirror DOM still shows the full URL, so we force it back to just the path.
      if (
        status === 'no-change' &&
        addressBarRef.value?.codeMirrorRef?.textContent &&
        addressBarRef.value.codeMirrorRef.textContent !== newPath
      ) {
        addressBarRef.value.setCodeMirrorContent(newPath)
      }

      // Re-trigger the click or focus event if we have a blur target selector
      if (blurTargetSelector) {
        const element = document.querySelector(blurTargetSelector)

        // Re-trigger clicks on buttons
        if (element instanceof HTMLButtonElement) {
          element.click()
        }

        // Re-trigger focus on inputs and codeInputs
        else if (
          element instanceof HTMLInputElement ||
          element instanceof HTMLTextAreaElement ||
          (element instanceof HTMLElement &&
            element.getAttribute('contenteditable') === 'true')
        ) {
          element.focus()
        }
      }
    },
  })
}

/** Update the operation's HTTP method, handling conflicts */
const handleMethodChange = (newMethod: HttpMethodType): void =>
  emitPathMethodUpdate(newMethod, pathConflict.value ?? path)

/**
 * Update the operation's path, handling conflicts also we extract the blur target selector to re-trigger click events
 *
 * We have special handling for the tab key to prevent it from triggering a click on the focused button
 */
const handlePathBlur = (newPath: string, event: FocusEvent): void => {
  const relatedTarget = event.relatedTarget as Element | null
  const blurTargetSelector = tabbedOut.value ? null : getSelector(relatedTarget)

  tabbedOut.value = false

  emitPathMethodUpdate(
    methodConflict.value ?? method,
    newPath,
    blurTargetSelector,
  )
}

/** Lets unset the server when backspace is pressed and the path is empty */
const handlePathBackspace = (event: KeyboardEvent): void => {
  if ((event.target as HTMLElement)?.innerText === '\n') {
    eventBus.emit('server:update:selected', {
      url: '',
      meta: serverMeta,
    })
  }
}

/** Handle path submit (Enter key) — saves the path and triggers execution via blurTargetSelector */
const handlePathSubmit = (
  newPath: string,
  event: KeyboardEvent | FocusEvent,
): void => {
  // Prevent the global hotkey listener
  event.stopPropagation()

  emitPathMethodUpdate(
    methodConflict.value ?? method,
    newPath,
    '[data-addressbar-action="send"]',
  )
}

/** Handle focus events */
const sendButtonRef = useTemplateRef('sendButtonRef')
const addressBarRef = useTemplateRef('addressBarRef')
const tabbedOut = ref(false)
const handleFocusSendButton = () => sendButtonRef.value?.$el?.focus()

const handleFocusAddressBar = (
  payload: ApiReferenceEvents['ui:focus:address-bar'],
) => {
  // If it already has focus we just propagate native behavior which should focus the browser address bar
  if (addressBarRef.value?.isFocused && layout !== 'desktop') {
    return
  }

  addressBarRef.value?.focus('end')

  if (payload && 'clear' in payload && payload.clear) {
    addressBarRef.value?.setCodeMirrorContent('/')
  }

  if (payload && 'event' in payload) {
    payload.event.preventDefault()
  }
}

onMounted(() => {
  eventBus.on('ui:focus:address-bar', handleFocusAddressBar)
  eventBus.on('ui:focus:send-button', handleFocusSendButton)
  eventBus.on('copy-url:address-bar', copyUrl)
  eventBus.on('hooks:on:request:sent', startLoading)
  eventBus.on('hooks:on:request:complete', stopLoading)
})

onBeforeUnmount(() => {
  eventBus.off('ui:focus:address-bar', handleFocusAddressBar)
  eventBus.off('ui:focus:send-button', handleFocusSendButton)
  eventBus.off('copy-url:address-bar', copyUrl)
  eventBus.off('hooks:on:request:sent', startLoading)
  eventBus.off('hooks:on:request:complete', stopLoading)

  // Stop the animation when the component is unmounted
  // This is to prevent the animation from continuing after the component is unmounted
  stopLoading()
})

const { copyToClipboard } = useClipboard()

/** Copy the resolved URL with the environment variables to the clipboard */
const copyUrl = async () => {
  const resolvedUrl = getResolvedUrl({ server, path })
  const environmentVariables = getEnvironmentVariables(environment)
  const resolvedUrlWithEnvVars = replaceEnvVariables(
    resolvedUrl,
    environmentVariables,
  )
  await copyToClipboard(resolvedUrlWithEnvVars)
}

const isServerDropdownOpen = ref(false)
const isHistoryDropdownOpen = ref(false)

/** Whether either dropdown is open */
const isDropdownOpen = computed(
  () => isServerDropdownOpen.value || isHistoryDropdownOpen.value,
)

const navigateToServersPage = () => {
  if (serverMeta.type === 'operation') {
    return eventBus.emit('ui:navigate', {
      page: 'operation',
      path: 'servers',
      operationPath: serverMeta.path,
      method: serverMeta.method,
    })
  }
  return eventBus.emit('ui:navigate', {
    page: 'document',
    path: 'servers',
  })
}

defineExpose({
  methodConflict,
  pathConflict,
})
</script>
<template>
  <div
    :id="id"
    class="scalar-address-bar order-last flex h-(--scalar-address-bar-height) w-full [--scalar-address-bar-height:32px] lg:order-0 lg:w-auto">
    <!-- Address Bar -->
    <div
      class="address-bar-bg-states text-xxs group relative order-last flex w-full max-w-[calc(100dvw-24px)] flex-1 flex-row items-stretch rounded-lg p-0.75 lg:order-none lg:max-w-[580px] lg:min-w-[580px] xl:max-w-[720px] xl:min-w-[720px]"
      :class="{
        'outline-c-danger outline': hasConflict,
        'rounded-b-none': isDropdownOpen,
      }">
      <div
        class="pointer-events-none absolute top-0 left-0 block h-full w-full overflow-hidden rounded-lg border"
        :class="{
          'rounded-b-none': isDropdownOpen,
        }">
        <div
          class="absolute top-0 left-0 h-full w-full"
          :style />
      </div>
      <div class="flex gap-1">
        <HttpMethod
          :isEditable="layout !== 'modal'"
          isSquare
          :method="methodConflict ?? method"
          teleport
          @change="handleMethodChange" />
      </div>

      <div
        class="scroll-timeline-x scroll-timeline-x-hidden relative flex w-full bg-blend-normal">
        <!-- Servers -->
        <ServerDropdown
          v-if="servers.length"
          :layout="layout"
          :meta="serverMeta"
          :server="server"
          :servers="servers"
          :target="id"
          @update:open="(value) => (isServerDropdownOpen = value)"
          @update:selectedServer="
            (payload) => eventBus.emit('server:update:selected', payload)
          "
          @update:servers="navigateToServersPage"
          @update:variable="
            (payload) => eventBus.emit('server:update:variables', payload)
          " />

        <div class="fade-left" />
        <!-- Path + URL + env vars -->
        <CodeInput
          ref="addressBarRef"
          alwaysEmitChange
          aria-label="Path"
          class="min-w-fit outline-none"
          disableCloseBrackets
          :disabled="layout === 'modal'"
          disableEnter
          disableTabIndent
          :emitOnBlur="false"
          :environment="environment"
          :extensions="[addressBarScrollMargins]"
          importCurl
          :layout="layout"
          :modelValue="path"
          :placeholder="server ? '' : 'Enter a URL'"
          server
          @blur="handlePathBlur"
          @keydown.delete="handlePathBackspace"
          @keydown.tab="tabbedOut = true"
          @submit="handlePathSubmit" />
        <div class="fade-right" />
      </div>

      <!-- Copy url button -->
      <ScalarButton
        class="hover:bg-b-3 mx-1"
        size="xs"
        variant="ghost"
        @click="copyUrl">
        <ScalarIconCopy />
        <span class="sr-only">Copy URL</span>
      </ScalarButton>

      <AddressBarHistory
        :history="history"
        :target="id"
        @select:history:item="(payload) => emit('select:history:item', payload)"
        @update:open="(value) => (isHistoryDropdownOpen = value)" />
      <!-- Error message -->
      <div
        v-if="hasConflict"
        class="z-context absolute inset-x-0 top-[calc(100%+4px)] flex flex-col items-center rounded px-6">
        <div
          class="text-c-danger bg-b-danger border-c-danger flex items-center gap-1 rounded border p-1">
          <ScalarIconWarningCircle size="sm" />
          <div class="min-w-0 flex-1">
            A
            <em>{{ methodConflict?.toUpperCase() ?? method.toUpperCase() }}</em>
            request to
            <ScalarWrappingText :text="pathConflict ?? path" />
            already exists in this document
          </div>
        </div>
      </div>

      <ScalarButton
        ref="sendButtonRef"
        class="relative h-auto shrink-0 overflow-hidden py-1 pr-2.5 pl-2 font-bold"
        data-addressbar-action="send"
        :disabled="isLoading"
        @click="emit('execute')">
        <span
          aria-hidden="true"
          class="inline-flex items-center gap-1">
          <ScalarIcon
            class="relative shrink-0 fill-current"
            icon="Play"
            size="xs" />
          <span class="text-xxs hidden lg:flex">Send</span>
        </span>
        <span class="sr-only">
          Send {{ method }} request to {{ server?.url ?? '' }}{{ path }}
        </span>
      </ScalarButton>
    </div>
  </div>
</template>
<style scoped>
:deep(.cm-editor) {
  height: 100%;
  outline: none;
  width: 100%;
}
:deep(.cm-line) {
  padding: 0;
}
:deep(.cm-content) {
  padding: 0;
  display: flex;
  align-items: center;
  font-size: var(--scalar-small);
}
.scroll-timeline-x {
  scroll-timeline: --scroll-timeline x;
  /* Firefox supports */
  scroll-timeline: --scroll-timeline horizontal;
  -ms-overflow-style: none; /* IE and Edge */
}
.scroll-timeline-x-hidden {
  overflow-x: auto;
}
.scroll-timeline-x-hidden :deep(.cm-scroller) {
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding-right: 20px;
  overflow: auto;
}
.scroll-timeline-x-hidden::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}
.scroll-timeline-x-hidden :deep(.cm-scroller::-webkit-scrollbar) {
  width: 0;
  height: 0;
  display: none;
}
.scroll-timeline-x-address {
  line-height: 27px;
  scrollbar-width: none; /* Firefox */
}
/* make clickable are to left of send button */
.scroll-timeline-x-address:after {
  content: '';
  position: absolute;
  height: 100%;
  width: 24px;
  right: 0;
  cursor: text;
}
.scroll-timeline-x-address:empty:before {
  content: 'Enter URL or cURL request';
  color: var(--scalar-color-3);
  pointer-events: none;
}
.fade-left,
.fade-right {
  content: '';
  position: sticky;
  height: 100%;
  animation-name: fadein;
  animation-duration: 1ms;
  animation-direction: reverse;
  animation-timeline: --scroll-timeline;
  pointer-events: none;
  z-index: 1;
}
.fade-left {
  background: linear-gradient(
    -90deg,
    color-mix(in srgb, var(--scalar-address-bar-bg), transparent 100%) 0%,
    color-mix(in srgb, var(--scalar-address-bar-bg), transparent 20%) 30%,
    var(--scalar-address-bar-bg) 100%
  );
  left: -1px;
  min-width: 6px;
  animation-direction: normal;
}
.fade-right {
  background: linear-gradient(
    90deg,
    color-mix(in srgb, var(--scalar-address-bar-bg), transparent 100%) 0%,
    color-mix(in srgb, var(--scalar-address-bar-bg), transparent 20%) 30%,
    var(--scalar-address-bar-bg) 100%
  );
  right: -1px;
  min-width: 24px;
}
@keyframes fadein {
  0% {
    opacity: 0;
  }
  1% {
    opacity: 1;
  }
}
.address-bar-bg-states {
  --scalar-address-bar-bg: color-mix(
    in srgb,
    var(--scalar-background-1),
    var(--scalar-background-2)
  );
  background: var(--scalar-address-bar-bg);
}
.address-bar-bg-states:has(.cm-focused) {
  --scalar-address-bar-bg: var(--scalar-background-1);
  border-color: var(--scalar-border-color);
  outline-width: 1px;
  outline-style: solid;
}
.address-bar-bg-states:has(.cm-focused) .fade-left,
.address-bar-bg-states:has(.cm-focused) .fade-right {
  --scalar-address-bar-bg: var(--scalar-background-1);
}
</style>
